import { useCallback, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Activity,
  Award,
  CheckSquare,
  FileText,
  Handshake,
  CalendarDays,
  History,
  Landmark,
  Layers,
  LayoutDashboard,
  MessageCircle,
  MessageSquare,
  PieChart,
  Scale,
  ScrollText,
  StickyNote,
  TrendingUp,
  Users,
} from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { PoolFormationDialog } from "./components/PoolFormationDialog";
import { SsnsTab } from "./tabs/SsnsTab";
import { FormationHistoryTab } from "./tabs/FormationHistoryTab";
import { Card } from "@/design-system/components/ui";
import {
  EntityTabBar,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";
import { useSpvPool, useSpvPoolMath } from "@/hooks/capitalMarkets/useSpvPools";
import { useSpvTrust } from "@/hooks/capitalMarkets/useSpvTrusts";
import { KpiCard, PoolStatusBadge } from "../shared/components";
import { formatCurrency, formatPct } from "../shared/format";
import { OverviewTab } from "./tabs/OverviewTab";
import { CompositionTab } from "./tabs/CompositionTab";
import { ConcentrationTab } from "./tabs/ConcentrationTab";
import { IDUPTab } from "./tabs/IDUPTab";
import { InvestorsTab } from "./tabs/InvestorsTab";
import { CashFlowTab } from "./tabs/CashFlowTab";
import { CertificationsTab } from "./tabs/CertificationsTab";
import { DocumentsTab } from "./tabs/DocumentsTab";
import { TasksTab } from "./tabs/TasksTab";
import { CalendarTab } from "./tabs/CalendarTab";
import { MessagesTab } from "./tabs/MessagesTab";
import { CommunicationsTab } from "./tabs/CommunicationsTab";
import { NotesTab } from "./tabs/NotesTab";
import { ActivityTab } from "./tabs/ActivityTab";
import { ClosingsTab } from "./components/ClosingsTab";
import { LegalReviewTab } from "./tabs/LegalReviewTab";

type TabId =
  | "overview"
  | "composition"
  | "concentration"
  | "idup"
  | "investors"
  | "closings"
  | "cashflow"
  | "certifications"
  | "legal-review"
  | "ssns"
  | "formation-history"
  | "documents"
  | "tasks"
  | "calendar"
  | "messages"
  | "communications"
  | "notes"
  | "activity";

const TABS: readonly TabId[] = [
  "overview",
  "composition",
  "concentration",
  "idup",
  "investors",
  "closings",
  "cashflow",
  "certifications",
  "legal-review",
  "ssns",
  "formation-history",
  "documents",
  "tasks",
  "calendar",
  "messages",
  "communications",
  "notes",
  "activity",
];

const TAB_ITEMS: ReadonlyArray<EntityTabItem<TabId>> = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "composition", label: "Composition", icon: Layers },
  { key: "concentration", label: "Concentration", icon: PieChart },
  { key: "idup", label: "IDUP", icon: ScrollText },
  { key: "investors", label: "Investors", icon: Users },
  { key: "closings", label: "Closings", icon: Handshake },
  { key: "cashflow", label: "Cash Flow", icon: TrendingUp },
  { key: "certifications", label: "Certifications", icon: Award },
  { key: "legal-review", label: "Legal Review", icon: Scale },
  { key: "ssns", label: "SSNs", icon: Landmark },
  { key: "formation-history", label: "Formation History", icon: History },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "tasks", label: "Tasks", icon: CheckSquare },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
  { key: "messages", label: "Messages", icon: MessageSquare },
  { key: "communications", label: "Communications", icon: MessageCircle },
  { key: "notes", label: "Notes", icon: StickyNote },
  { key: "activity", label: "Activity", icon: Activity },
];

function isTabId(value: string | null): value is TabId {
  return value !== null && (TABS as readonly string[]).includes(value);
}

export function PoolDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const poolQuery = useSpvPool(id);
  const pool = poolQuery.data;
  const trustQuery = useSpvTrust(pool?.spvId);
  const mathQuery = useSpvPoolMath(id);

  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const tab: TabId = isTabId(tabParam) ? tabParam : "overview";
  const [formationOpen, setFormationOpen] = useState(false);

  const setTab = useCallback(
    (next: TabId) => {
      const updated = new URLSearchParams(params);
      if (next === "overview") updated.delete("tab");
      else updated.set("tab", next);
      setParams(updated, { replace: true });
    },
    [params, setParams],
  );

  if (poolQuery.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading pool…</p>
      </Card>
    );
  }

  if (!pool) {
    return (
      <Card>
        <h2 className="font-display font-bold text-h2">Pool not found</h2>
        <p className="text-small text-gray-mid">
          The SPV pool you requested was not found or you don&apos;t have
          permission to view it.
        </p>
        <button
          type="button"
          className="mt-space-3 text-gold-shadow hover:underline text-small"
          onClick={() => navigate("/capital-markets/pools")}
        >
          Back to pools
        </button>
      </Card>
    );
  }

  const math = mathQuery.data;

  return (
    <div className="flex flex-col gap-space-3">
      <div className="flex flex-col gap-space-2">
        <div className="flex flex-wrap items-center gap-space-3">
          <h1 className="font-display font-bold text-h2 text-onyx m-0">
            {pool.poolName}
          </h1>
          <PoolStatusBadge status={pool.status} />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-space-2">
          <div className="text-small text-gray-mid">
            SPV Trust:{" "}
            <Link
              to={`/capital-markets/trusts/${pool.spvId}`}
              className="text-gold-shadow hover:underline"
            >
              {trustQuery.data?.trustName ?? "View trust"}
            </Link>
          </div>
          {pool.status === "InvestorCommitment" && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setFormationOpen(true)}
              data-testid="pool-finalize-button"
            >
              Finalize Pool
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-space-3">
        <KpiCard
          label="Contributed Principal"
          value={formatCurrency(math?.contributedPrincipal)}
          hint={`Target ${formatCurrency(pool.targetPoolSize)}`}
        />
        <KpiCard
          label="Contribution %"
          value={formatPct(math?.contributionPctPrincipal)}
        />
        <KpiCard
          label="Investment Fill %"
          value={formatPct(math?.investmentFillPct)}
        />
        <KpiCard label="Reserve Fund" value="—" hint="Available in PR-6" />
      </div>

      <EntityTabBar
        ariaLabel="Pool sections"
        testIdPrefix="spv-pool"
        items={TAB_ITEMS}
        value={tab}
        onChange={setTab}
      />

      <div className="pt-space-4">
        {tab === "overview" && (
          <OverviewTab
            pool={pool}
            onOpenTab={(t) => setTab(t)}
          />
        )}
        {tab === "composition" && <CompositionTab pool={pool} />}
        {tab === "concentration" && <ConcentrationTab pool={pool} />}
        {tab === "idup" && <IDUPTab pool={pool} />}
        {tab === "investors" && <InvestorsTab pool={pool} />}
        {tab === "closings" && <ClosingsTab pool={pool} />}
        {tab === "cashflow" && <CashFlowTab pool={pool} />}
        {tab === "certifications" && <CertificationsTab pool={pool} />}
        {tab === "legal-review" && <LegalReviewTab pool={pool} />}
        {tab === "ssns" && <SsnsTab pool={pool} />}
        {tab === "formation-history" && <FormationHistoryTab pool={pool} />}
        {tab === "documents" && (
          <DocumentsTab id={pool.id} name={pool.poolName} />
        )}
        {tab === "tasks" && <TasksTab id={pool.id} />}
        {tab === "calendar" && (
          <CalendarTab id={pool.id} name={pool.poolName} />
        )}
        {tab === "messages" && (
          <MessagesTab id={pool.id} name={pool.poolName} />
        )}
        {tab === "communications" && (
          <CommunicationsTab id={pool.id} name={pool.poolName} />
        )}
        {tab === "notes" && <NotesTab id={pool.id} />}
        {tab === "activity" && <ActivityTab id={pool.id} />}
      </div>

      <PoolFormationDialog
        open={formationOpen}
        onClose={() => setFormationOpen(false)}
        pool={pool}
        versionId={0}
      />
    </div>
  );
}
